/**
 * Wizard Step 1 — Profile.
 *
 * Collects the minimum fields needed to know who the user is:
 * first name, last name, DOB, optional phone. No profile picture —
 * that's pushed to a dashboard task per the onboarding plan.
 *
 * Saves to the existing user profile endpoint, then navigates to the
 * Household step.
 */
import React, { useState } from 'react';
import { View, Text, StyleSheet, TextInput, Alert, Platform } from 'react-native';
import DateTimePickerModal from 'react-native-modal-datetime-picker';
import { useNavigation } from '@react-navigation/native';
import { WizardShell } from '../components/WizardShell';
import { ms } from '../../../utils/responsive';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../../config';
import axios from 'axios';
import { createLogger } from '../../../utils/logger';
import { analytics } from '../../../services/analytics/analytics';
import { AnalyticsEvent } from '../../../services/analytics/events';
import { getAuthToken } from '../../../services/auth/tokenStorage';

const logger = createLogger('WizardProfile');
const WIZARD_STEP = 1;
const TOTAL_STEPS = 6;

export const WizardProfileScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState<Date | null>(null);
  const [phone, setPhone] = useState('');
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill from the User row so returning v0 migrators and even fresh
  // signups (who already entered firstName/lastName at registration)
  // don't have to retype. Runs once on mount; silently no-ops if
  // fields are already empty or the fetch fails.
  React.useEffect(() => {
    analytics.track(AnalyticsEvent.WizardStepShown, { step: 'profile', number: WIZARD_STEP });
    (async () => {
      try {
        const token = await getAuthToken();
        if (!token) return;
        const res = await axios.get(`${API_URL}/users/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const user = res.data?.user;
        if (!user) return;
        if (user.firstName) setFirstName(user.firstName);
        if (user.lastName) setLastName(user.lastName);
        if (user.phoneNumber) setPhone(user.phoneNumber);
        if (user.dateOfBirth) {
          const parsed = new Date(user.dateOfBirth);
          if (!isNaN(parsed.getTime())) setDob(parsed);
        }
      } catch (err) {
        logger.debug('Profile pre-fill fetch failed (non-fatal)', err);
      }
    })();
  }, []);

  const canSubmit = firstName.trim().length > 0 && lastName.trim().length > 0 && dob !== null;

  const handleContinue = async () => {
    if (!canSubmit || !dob) return;
    setSubmitting(true);
    try {
      const token = await getAuthToken();
      // Writes to the User row (same one EditProfileScreen edits).
      // Scoped wizard endpoint so we don't need the email / username
      // fields that /users/update-profile requires.
      await axios.put(
        `${API_URL}/api/onboarding/profile`,
        {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          dateOfBirth: dob.toISOString().split('T')[0],
          phoneNumber: phone.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      analytics.track(AnalyticsEvent.WizardStepCompleted, { step: 'profile' });
      navigation.navigate('WizardHousehold');
    } catch (err) {
      logger.error('Failed to save profile', err);
      Alert.alert('Could not save', 'Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardShell
      stepNumber={WIZARD_STEP}
      totalSteps={TOTAL_STEPS}
      title="Tell us about yourself"
      subtitle="We just need the basics. You can fill in everything else later."
      primaryLabel="Continue"
      primaryDisabled={!canSubmit}
      primaryLoading={submitting}
      onPrimaryPress={handleContinue}
    >
      <View style={styles.form}>
        <Text style={styles.label}>First name</Text>
        <TextInput
          style={styles.input}
          value={firstName}
          onChangeText={setFirstName}
          placeholder="Jane"
          placeholderTextColor="#9CA3AF"
          autoCapitalize="words"
          autoComplete="given-name"
        />

        <Text style={styles.label}>Last name</Text>
        <TextInput
          style={styles.input}
          value={lastName}
          onChangeText={setLastName}
          placeholder="Doe"
          placeholderTextColor="#9CA3AF"
          autoCapitalize="words"
          autoComplete="family-name"
        />

        <Text style={styles.label}>Date of birth</Text>
        <View
          style={styles.input}
          onTouchEnd={() => setDatePickerVisible(true)}
        >
          <Text style={dob ? styles.inputText : styles.inputPlaceholder}>
            {dob ? dob.toLocaleDateString() : 'MM / DD / YYYY'}
          </Text>
        </View>
        <DateTimePickerModal
          isVisible={datePickerVisible}
          mode="date"
          maximumDate={new Date()}
          onConfirm={d => {
            setDob(d);
            setDatePickerVisible(false);
          }}
          onCancel={() => setDatePickerVisible(false)}
        />

        <Text style={styles.label}>Phone number <Text style={styles.optional}>(optional)</Text></Text>
        <TextInput
          style={styles.input}
          value={phone}
          onChangeText={setPhone}
          placeholder="(555) 123-4567"
          placeholderTextColor="#9CA3AF"
          keyboardType="phone-pad"
          autoComplete="tel"
        />
      </View>
    </WizardShell>
  );
};

const styles = StyleSheet.create({
  form: {
    marginTop: ms(4),
  },
  label: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginTop: ms(14),
    marginBottom: ms(6),
  },
  optional: {
    fontWeight: '400',
    color: '#6B7280',
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(12),
    paddingHorizontal: ms(14),
    paddingVertical: ms(14),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#E1E5EA',
    justifyContent: 'center',
  },
  inputText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  inputPlaceholder: {
    fontSize: ms(16, 0.3),
    color: '#9CA3AF',
  },
});
